import type { HasAverageColor } from "./HasAverageColor";
import type { HasBackgroundColor } from "./HasBackgroundColor";
import {
  BackgroundVirtualPixel,
  ConstantVirtualPixel,
  EdgeVirtualPixel,
  HorizontalTileEdgeVirtualPixel,
  HorizontalTileVirtualPixel,
  MirrorVirtualPixel,
  RandomVirtualPixel,
  TileVirtualPixel,
  VerticalTileEdgeVirtualPixel,
  VerticalTileVirtualPixel,
  type VirtualPixelDecorator,
} from "./virtual-pixel-decorator";
import type { VirtualPixelDecoratorApplicator } from "./VirtualPixelDecoratorApplicator";
import { VirtualPixelMethod } from "./VirtualPixelMethod";
import type { VirtualViewportPixelAccessor } from "./VirtualViewportPixelAccessor";

/**
 * Base implementation of VirtualPixelDecoratorApplicator
 */
export class BaseVirtualPixelDecoratorApplicator
  implements VirtualPixelDecoratorApplicator
{
  /**
   * @inheritDoc
   */
  decorate<
    DecoratedType extends VirtualViewportPixelAccessor &
      HasAverageColor &
      HasBackgroundColor
  >(
    decorated: DecoratedType,
    method: VirtualPixelMethod
  ): VirtualPixelDecorator<DecoratedType> {
    switch (method) {
      case VirtualPixelMethod.BACKGROUND:
        return new BackgroundVirtualPixel(decorated);
      case VirtualPixelMethod.EDGE:
        return new EdgeVirtualPixel(decorated);
      case VirtualPixelMethod.MIRROR:
        return new MirrorVirtualPixel(decorated);
      case VirtualPixelMethod.RANDOM:
        return new RandomVirtualPixel(decorated);
      case VirtualPixelMethod.TILE:
        return new TileVirtualPixel(decorated);
      case VirtualPixelMethod.TRANSPARENT:
        return new ConstantVirtualPixel(decorated, [0, 0, 0, 0]);
      case VirtualPixelMethod.BLACK:
        return new ConstantVirtualPixel(decorated, [
          0,
          0,
          0,
          decorated.getQuantumRange(),
        ]);
      case VirtualPixelMethod.GRAY:
        return new ConstantVirtualPixel(decorated, [
          Math.floor(decorated.getQuantumRange() / 2),
          Math.floor(decorated.getQuantumRange() / 2),
          Math.floor(decorated.getQuantumRange() / 2),
          decorated.getQuantumRange(),
        ]);
      case VirtualPixelMethod.WHITE:
        return new ConstantVirtualPixel(decorated, [
          decorated.getQuantumRange(),
          decorated.getQuantumRange(),
          decorated.getQuantumRange(),
          decorated.getQuantumRange(),
        ]);
      case VirtualPixelMethod.HORIZONTAL_TILE:
        return new HorizontalTileVirtualPixel(decorated);
      case VirtualPixelMethod.VERTICAL_TILE:
        return new VerticalTileVirtualPixel(decorated);
      case VirtualPixelMethod.HORIZONTAL_TILE_EDGE:
        return new HorizontalTileEdgeVirtualPixel(decorated);
      case VirtualPixelMethod.VERTICAL_TILE_EDGE:
        return new VerticalTileEdgeVirtualPixel(decorated);
      default:
        throw new TypeError(`Unknown Virtual Pixel Method "${method}" given.`);
    }
  }
}
